import { useState, useEffect, useCallback, useRef } from 'react';
import { googleLogin, sendOtp, verifyOtp, checkEmailExists, decideSendOtp, authBlockMessage } from '../api';
import Legal from './Legal';
import './Login.css';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

function isBackendDown(msg) {
  if (!msg) return false;
  const m = msg.toLowerCase();
  return (
    m.includes('unable to reach') ||
    m.includes('temporarily unavailable') ||
    m.includes('not reachable') ||
    m.includes('network error')
  );
}

const GOOGLE_SVG = (
  <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    <path fill="none" d="M0 0h48v48H0z"/>
  </svg>
);

function Logo() {
  return (
    <div className="login-logo">
      <div className="login-logo-icon">🔆</div>
      <div className="login-logo-text">Good<span>GPT</span></div>
    </div>
  );
}

function decodeGoogleCredential(credential) {
  try {
    const payload = credential?.split('.')[1];
    if (!payload) return {};

    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const decoded = decodeURIComponent(
      Array.from(atob(padded), (char) => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`).join('')
    );
    return JSON.parse(decoded);
  } catch {
    return {};
  }
}

export default function Login({ onLogin, onGuest, initialMode = null }) {
  const [legalTab, setLegalTab] = useState(null);

  // mode: null (choice) | 'login' | 'signup'
  const [mode, setMode] = useState(initialMode);
  // step: 'email' | 'otp'
  const [step, setStep] = useState('email');

  // shared fields
  const [email, setEmail]     = useState('');
  const [otp, setOtp]         = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [info, setInfo]       = useState('');
  const [backendDown, setBackendDown] = useState(false);

  const googleReady = useRef(false);
  const googleButtonRef = useRef(null);
  const [googleScriptLoaded, setGoogleScriptLoaded] = useState(false);
  const handleGoogleCredentialRef = useRef(null);

  const handleGoogleCredential = useCallback(async (response) => {
    if (!response?.credential) {
      setError('Google did not return a sign-in token. Please try again.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const payload = decodeGoogleCredential(response.credential);
      const data = await googleLogin(response.credential, payload);
      const token = data.token || data.access || data.access_token || data.auth_token || data.data?.token;
      const user = data.user || data.data?.user || {
        email_id: data.email_id || payload.email || '',
        name: data.name || payload.name || '',
        picture: data.picture || payload.picture || '',
      };

      if (data.success && token) {
        onLogin(user, token);
        return;
      }

      setError(data.message || 'Google sign-in failed.');
    } catch (err) {
      setError(err?.message || 'Google sign-in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [onLogin]);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;

    if (window.google?.accounts?.id) {
      googleReady.current = true;
      setGoogleScriptLoaded(true);
      return;
    }

    if (googleReady.current) return;
    const existing = document.getElementById('gsi-script');
    if (existing) {
      const markLoaded = () => setGoogleScriptLoaded(true);
      if (window.google?.accounts?.id) {
        markLoaded();
        return;
      }
      existing.addEventListener('load', markLoaded, { once: true });
      return () => existing.removeEventListener('load', markLoaded);
    }

    googleReady.current = true;
    const script = document.createElement('script');
    script.id = 'gsi-script';
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => { setGoogleScriptLoaded(true); };
    script.onerror = () => { setError('Google sign-in is not available right now.'); };
    document.head.appendChild(script);
  }, []);

  // Keep the credential handler ref current on every render so the initialize
  // callback always calls the latest version without needing to re-initialize.
  useEffect(() => {
    handleGoogleCredentialRef.current = handleGoogleCredential;
  });

  // Initialize Google Identity Services once when the script loads.
  // Calling initialize() more than once confuses the SDK and can break the popup.
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || !googleScriptLoaded || !window.google?.accounts?.id) return;
    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: (response) => handleGoogleCredentialRef.current(response),
      ux_mode: 'popup',
    });
  }, [googleScriptLoaded]);

  // Re-render the button whenever the visible view changes (mode / step).
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || !googleScriptLoaded || !window.google?.accounts?.id || !googleButtonRef.current) return;
    googleButtonRef.current.innerHTML = '';
    window.google.accounts.id.renderButton(googleButtonRef.current, {
      theme: 'outline',
      size: 'large',
      text: 'continue_with',
      shape: 'rectangular',
      width: Math.min(360, googleButtonRef.current.offsetWidth || 360),
    });
  }, [googleScriptLoaded, mode, step]);

  const renderGoogleSignIn = () => {
    if (!GOOGLE_CLIENT_ID) {
      return (
        <button type="button" className="btn-google" disabled>
          {GOOGLE_SVG} Google sign-in unavailable
        </button>
      );
    }

    return (
      <div className="google-button-shell">
        <div ref={googleButtonRef} className="google-button-render-target" />
        {!googleScriptLoaded && (
          <button type="button" className="btn-google" disabled>
            {GOOGLE_SVG} Loading Google...
          </button>
        )}
      </div>
    );
  };

  const renderBackendNotice = () => {
    if (!backendDown) return null;
    return (
      <div className="firebase-fallback">
        <p className="firebase-fallback-note">
          Our servers look unreachable right now. Please try again in a moment.
        </p>
      </div>
    );
  };

  // signup-only fields
  const [name, setName]                   = useState('');
  const [mobile, setMobile]               = useState('');

  const reset = () => {
    setStep('email'); setEmail(''); setOtp('');
    setName(''); setMobile('');
    setError(''); setInfo('');
  };

  const switchTo = (newMode) => { reset(); setMode(newMode); };

  useEffect(() => {
    setMode(initialMode);
    setStep('email');
    setError('');
    setInfo('');
  }, [initialMode]);

  // ── Send OTP ─────────────────────────────────────────────────────────────────
  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    const addr = email.trim().toLowerCase();
    setError(''); setInfo(''); setLoading(true);
    try {
      // Check the account exists before spending an OTP on it. Sign-in only
      // proceeds for a confirmed account; sign-up only for an unconfirmed one.
      // An unavailable check blocks sign-in rather than mailing a stranger.
      const accountState = await checkEmailExists(addr);
      const decision = decideSendOtp(accountState, mode);
      if (!decision.send) {
        setError(authBlockMessage(decision.reason, addr));
        return;
      }

      const data = await sendOtp(addr, mode);
      if (data.success) {
        setInfo(`OTP sent to ${addr}`);
        setStep('otp');
      } else {
        if (isBackendDown(data.message)) setBackendDown(true);
        setError(data.message || 'Failed to send OTP.');
      }
    } catch {
      setBackendDown(true);
      setError('Network error — server unreachable.');
    } finally {
      setLoading(false);
    }
  };

  // ── Verify OTP ───────────────────────────────────────────────────────────────
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otp.trim()) return;
    setError(''); setLoading(true);
    try {
      const data = await verifyOtp(
        email.trim().toLowerCase(),
        otp.trim(),
        mode === 'signup' ? name.trim() : '',
        mode === 'signup' ? mobile.trim() : '',
        '',
      );
      if (data.success && data.token) {
        onLogin(data.user, data.token);
      } else {
        if (isBackendDown(data.message)) setBackendDown(true);
        setError(data.message || 'Invalid OTP.');
      }
    } catch {
      setBackendDown(true);
      setError('Network error — server unreachable.');
    } finally {
      setLoading(false);
    }
  };

  // ── Choice screen ─────────────────────────────────────────────────────────────
  if (mode === null) {
    return (
      <div className="login-page">
        <div className="login-card">
          <Logo />
          <h1 className="login-title">Welcome to GoodGPT</h1>
          <p className="login-subtitle">Your AI-powered learning and shopping companion</p>

          <div className="login-choice-btns">
            <button className="btn-primary" onClick={() => setMode('login')}>
              Sign In
            </button>
            <button className="btn-choice-secondary" onClick={() => setMode('signup')}>
              Create Account
            </button>
          </div>

          <div className="login-divider"><span>or</span></div>
          {renderGoogleSignIn()}
        {renderBackendNotice()}

        <p className="login-legal-note">
            By continuing you agree to our{' '}
            <button className="legal-link" onClick={() => setLegalTab('terms')}>Terms &amp; Conditions</button>
            {' '}and{' '}
            <button className="legal-link" onClick={() => setLegalTab('privacy')}>Privacy Policy</button>.
          </p>
        </div>
        {legalTab && <Legal initialTab={legalTab} onClose={() => setLegalTab(null)} />}
      </div>
    );
  }

  // ── Login flow ────────────────────────────────────────────────────────────────
  if (mode === 'login') {
    return (
      <div className="login-page">
        <div className="login-card">
          <Logo />

          {step === 'email' && (
            <>
              <h1 className="login-title">Sign in</h1>
              <p className="login-subtitle">Enter your email to receive a one-time code</p>
              <form className="login-form" onSubmit={handleSendOtp}>
                <div className="form-group">
                  <label className="form-label">Email address</label>
                  <input
                    className="form-input"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    required autoFocus
                  />
                </div>
                {error && (
                  <div className="login-error">
                    {error}
                    {(error.toLowerCase().includes('not found') || error.toLowerCase().includes('sign up') || error.toLowerCase().includes('no account')) && (
                      <button type="button" className="login-error-switch" onClick={() => switchTo('signup')}>
                        Create an account →
                      </button>
                    )}
                  </div>
                )}
                {info && <div className="login-success">{info}</div>}
                <button className="btn-primary" type="submit" disabled={loading || !email.trim()}>
                  {loading ? 'Sending…' : 'Send OTP'}
                </button>
              </form>

              <div className="login-divider"><span>or</span></div>
              {renderGoogleSignIn()}
            </>
          )}

          {step === 'otp' && (
            <>
              <h1 className="login-title">Check your email</h1>
              <p className="login-subtitle">
                Enter the 6-digit code sent to <strong>{email}</strong>
              </p>
              <form className="login-form" onSubmit={handleVerifyOtp}>
                <div className="form-group">
                  <label className="form-label">OTP Code</label>
                  <input
                    className="form-input otp-input"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="------"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                    disabled={loading}
                    autoFocus required
                  />
                </div>
                {info && <div className="login-success">{info}</div>}
                {error && <div className="login-error">{error}</div>}
                <button className="btn-primary" type="submit" disabled={loading || otp.length < 6}>
                  {loading ? 'Verifying…' : 'Sign In'}
                </button>
                <button type="button" className="resend-link"
                  onClick={() => { setStep('email'); setOtp(''); setError(''); setInfo(''); }}
                  disabled={loading}
                >
                  ← Change email or resend
                </button>
              </form>
            </>
          )}

          <div className="login-switch-row">
            Don't have an account?{' '}
            <button className="login-switch-link" onClick={() => switchTo('signup')}>Sign up</button>
          </div>

          {renderBackendNotice()}

          <p className="login-legal-note">
            By signing in you agree to our{' '}
            <button className="legal-link" onClick={() => setLegalTab('terms')}>Terms &amp; Conditions</button>
            {' '}and{' '}
            <button className="legal-link" onClick={() => setLegalTab('privacy')}>Privacy Policy</button>.
          </p>
        </div>
        {legalTab && <Legal initialTab={legalTab} onClose={() => setLegalTab(null)} />}
      </div>
    );
  }

  // ── Signup flow ───────────────────────────────────────────────────────────────
  return (
    <div className="login-page">
      <div className="login-card">
        <Logo />

        {step === 'email' && (
          <>
            <h1 className="login-title">Create account</h1>
            <p className="login-subtitle">Join GoodGPT to start learning and shopping</p>
            <form className="login-form" onSubmit={handleSendOtp}>
              <div className="form-group">
                <label className="form-label">Email address</label>
                <input
                  className="form-input"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  required autoFocus
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    Name <span style={{ opacity: 0.5, fontWeight: 400 }}>(optional)</span>
                  </label>
                  <input
                    className="form-input"
                    type="text"
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={loading}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Mobile <span className="form-optional">(optional)</span></label>
                  <input
                    className="form-input"
                    type="tel"
                    placeholder="+91 98765 43210"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                    disabled={loading}
                  />
                </div>
              </div>
              {error && (
                <div className="login-error">
                  {error}
                  {(error.toLowerCase().includes('already') || error.toLowerCase().includes('exists') || error.toLowerCase().includes('sign in') || error.toLowerCase().includes('log in')) && (
                    <button type="button" className="login-error-switch" onClick={() => switchTo('login')}>
                      Sign in instead →
                    </button>
                  )}
                </div>
              )}
              {info && <div className="login-success">{info}</div>}
              <button className="btn-primary" type="submit"
                disabled={loading || !email.trim()}
              >
                {loading ? 'Sending…' : 'Send OTP'}
              </button>
            </form>
          </>
        )}

        {step === 'otp' && (
          <>
            <h1 className="login-title">Verify your email</h1>
            <p className="login-subtitle">
              Enter the 6-digit code sent to <strong>{email}</strong>
            </p>
            <form className="login-form" onSubmit={handleVerifyOtp}>
              <div className="form-group">
                <label className="form-label">OTP Code</label>
                <input
                  className="form-input otp-input"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="------"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  disabled={loading}
                  autoFocus required
                />
              </div>
              {info && <div className="login-success">{info}</div>}
              {error && <div className="login-error">{error}</div>}
              <button className="btn-primary" type="submit" disabled={loading || otp.length < 6}>
                {loading ? 'Verifying…' : 'Create Account'}
              </button>
              <button type="button" className="resend-link"
                onClick={() => { setStep('email'); setOtp(''); setError(''); setInfo(''); }}
                disabled={loading}
              >
                ← Change email or resend
              </button>
            </form>
          </>
        )}

        <div className="login-switch-row">
          Already have an account?{' '}
          <button className="login-switch-link" onClick={() => switchTo('login')}>Sign in</button>
        </div>

          <div className="login-divider"><span>or</span></div>
          {renderGoogleSignIn()}
          {renderBackendNotice()}

          <p className="login-legal-note">
          By continuing you agree to our{' '}
          <button className="legal-link" onClick={() => setLegalTab('terms')}>Terms &amp; Conditions</button>
          {' '}and{' '}
          <button className="legal-link" onClick={() => setLegalTab('privacy')}>Privacy Policy</button>.
        </p>
      </div>
      {legalTab && <Legal initialTab={legalTab} onClose={() => setLegalTab(null)} />}
    </div>
  );
}
